import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';

interface SparklineCardProps {
  title: string;
  value: string;
  subValue?: string;
  data: number[];
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
  color?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const SparklineCard: React.FC<SparklineCardProps> = ({
  title,
  value,
  subValue,
  data,
  trend = 'stable',
  trendValue = 0,
  color = '#6B1786',
  icon,
  onClick,
}) => {
  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-green-600 bg-green-50';
      case 'down':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border border-gray-200 p-4 ${
        onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon && (
            <div className="p-2 rounded-lg bg-gray-100" style={{ color }}>
              {icon}
            </div>
          )}
          <span className="text-sm font-medium text-gray-600">{title}</span>
        </div>
        {trendValue !== 0 && (
          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getTrendColor()}`}>
            {getTrendIcon()}
            <span>{Math.abs(trendValue).toFixed(1)}%</span>
          </div>
        )}
      </div>

      <div className="mb-3">
        <span className="text-2xl font-bold text-gray-900" style={{ whiteSpace: 'pre-line' }}>{value}</span>
        {subValue && <div className="text-sm text-gray-500">{subValue}</div>}
      </div>

      {data.length > 0 && (
        <div className="h-12">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.map((value, index) => ({ value, index }))}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default SparklineCard;
